import {
    Alert,
    Button,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Switch,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {Link} from '@tanstack/react-router'
import {useEffect, useState} from 'react'

import {requiresHomeYard} from 'src/assets/workers/home-yard'
import {AssignmentStrip} from 'src/assignments/assignment-strip'
import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {useDispatchYards} from 'src/dispatch/use-dispatch'
import {formatTimeEntryRange} from 'src/jobs/job-time-entry-format'
import {ConfirmDialog} from 'src/ui/confirm-dialog'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import type {WorkerType} from '../worker-types/schemas'
import {useWorkerTypes} from '../worker-types/use-worker-types'
import {
    type EmploymentClassification,
    employmentClassificationLabel,
    employmentClassificationOptions,
} from './schemas'
import {
    useArchiveWorker,
    useUnarchiveWorker,
    useUpdateWorker,
    useWorker,
    useWorkerAssignments,
    useWorkerTimeEntries,
} from './use-workers'
import {validateWorkerName} from './worker-name-validation'

export type WorkerDetailsPageState =
    | 'loading'
    | 'error'
    | 'notFound'
    | 'reading'
    | 'editing'
    | 'saving'

type WorkerFormValues = {
    name: string
    workerTypeId: string | null
    employmentClassification: EmploymentClassification | null
    email: string
    phone: string
    homeDispatchYardId: string | null
    isPlaceholder: boolean
}

const PLACEHOLDER_LABEL = 'Placeholder worker'

type WorkerDetailsPageProps = {
    workerId: string
}

function validateWorkerForm(values: WorkerFormValues, workerTypes: WorkerType[]) {
    const errors: Partial<Record<keyof WorkerFormValues, string>> = {}
    const nameError = validateWorkerName(values.name)
    if (nameError) {
        errors.name = nameError
    }
    if (!values.workerTypeId) {
        errors.workerTypeId = 'Worker type is required'
    }
    if (!values.employmentClassification) {
        errors.employmentClassification = 'Employment classification is required'
    }
    if (!values.homeDispatchYardId && requiresHomeYard(workerTypes, values.workerTypeId)) {
        errors.homeDispatchYardId = 'Yard is required'
    }
    return errors
}

function isNotFoundError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to load worker'
    return message.toLowerCase().includes('not found')
}

export function WorkerDetailsPage({workerId}: WorkerDetailsPageProps) {
    const user = useCurrentUser()
    const {data: worker, isLoading, isError, error, refetch} = useWorker(workerId)
    const {
        data: assignmentList,
        isLoading: assignmentsLoading,
        isError: assignmentsError,
        error: assignmentsLoadError,
        refetch: refetchAssignments,
        referenceDate,
    } = useWorkerAssignments(workerId, {enabled: Boolean(worker)})
    const {
        data: timeEntryList,
        isLoading: timeEntriesLoading,
        isError: timeEntriesError,
        error: timeEntriesLoadError,
        refetch: refetchTimeEntries,
    } = useWorkerTimeEntries(workerId, {enabled: Boolean(worker)})
    const {data: workerTypes = []} = useWorkerTypes()
    const {data: yards = []} = useDispatchYards()
    const updateWorker = useUpdateWorker()
    const archiveWorker = useArchiveWorker()
    const unarchiveWorker = useUnarchiveWorker()
    const [pageState, setPageState] = useState<WorkerDetailsPageState>('loading')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [archiveModalOpen, setArchiveModalOpen] = useState(false)

    const form = useForm<WorkerFormValues>({
        initialValues: {
            name: '',
            workerTypeId: null,
            employmentClassification: null,
            email: '',
            phone: '',
            homeDispatchYardId: null,
            isPlaceholder: false,
        },
        validate: (values) => validateWorkerForm(values, workerTypes),
    })

    useEffect(() => {
        if (isLoading) {
            setPageState('loading')
            return
        }

        if (isError) {
            if (isNotFoundError(error)) {
                setPageState('notFound')
                return
            }

            setErrorMessage(error instanceof Error ? error.message : 'Failed to load worker')
            setPageState('error')
            return
        }

        if (worker) {
            form.setValues({
                name: worker.name,
                workerTypeId: worker.workerTypeId,
                employmentClassification: worker.employmentClassification,
                email: worker.email ?? '',
                phone: worker.phone ?? '',
                homeDispatchYardId: worker.homeDispatchYardId,
                isPlaceholder: worker.isPlaceholder,
            })
            if (pageState === 'loading' || pageState === 'error') {
                setPageState('reading')
            }
        }
    }, [worker, isLoading, isError, error])

    function handleEdit() {
        if (!worker) {
            return
        }

        form.setValues({
            name: worker.name,
            workerTypeId: worker.workerTypeId,
            employmentClassification: worker.employmentClassification,
            email: worker.email ?? '',
            phone: worker.phone ?? '',
            homeDispatchYardId: worker.homeDispatchYardId,
            isPlaceholder: worker.isPlaceholder,
        })
        setPageState('editing')
    }

    function handleCancel() {
        if (!worker) {
            return
        }

        form.setValues({
            name: worker.name,
            workerTypeId: worker.workerTypeId,
            employmentClassification: worker.employmentClassification,
            email: worker.email ?? '',
            phone: worker.phone ?? '',
            homeDispatchYardId: worker.homeDispatchYardId,
            isPlaceholder: worker.isPlaceholder,
        })
        setPageState('reading')
    }

    async function handleSave() {
        if (!worker || !form.values.workerTypeId) {
            return
        }

        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        setPageState('saving')
        setErrorMessage(null)

        try {
            await updateWorker.mutateAsync({
                id: worker.id,
                input: {
                    name: form.values.name.trim(),
                    workerTypeId: form.values.workerTypeId,
                    employmentClassification: form.values.employmentClassification,
                    email: form.values.email,
                    phone: form.values.phone,
                    ...(requiresHomeYard(workerTypes, form.values.workerTypeId)
                        ? {homeDispatchYardId: form.values.homeDispatchYardId}
                        : {}),
                    ...(form.values.isPlaceholder !== worker.isPlaceholder
                        ? {isPlaceholder: form.values.isPlaceholder}
                        : {}),
                },
            })
            setPageState('reading')
        } catch (saveError) {
            const message = saveError instanceof Error ? saveError.message : 'Failed to save worker'
            setErrorMessage(message)
            setPageState('editing')
        }
    }

    async function handleArchiveConfirm() {
        if (!worker) {
            return
        }
        try {
            await archiveWorker.mutateAsync(worker.id)
            setArchiveModalOpen(false)
        } catch (archiveError) {
            setArchiveModalOpen(false)
            setErrorMessage(
                archiveError instanceof Error ? archiveError.message : 'Failed to archive worker',
            )
        }
    }

    async function handleUnarchive() {
        if (!worker) {
            return
        }
        try {
            await unarchiveWorker.mutateAsync(worker.id)
        } catch (unarchiveError) {
            setErrorMessage(
                unarchiveError instanceof Error
                    ? unarchiveError.message
                    : 'Failed to unarchive worker',
            )
        }
    }

    if (pageState === 'loading') {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (pageState === 'notFound') {
        return (
            <Stack gap="md">
                <Title order={1}>Worker not found</Title>
                <Text c="dimmed">This worker may have been removed or the link is invalid.</Text>
                <DetailPageBackLink to="/workers" listLabel="workers" />
            </Stack>
        )
    }

    if (pageState === 'error' || !worker) {
        return (
            <Stack gap="md">
                <Title order={1}>Worker details</Title>
                <Alert color="red" title="Could not load worker">
                    {errorMessage ?? 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void refetch()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    const isEditing = pageState === 'editing' || pageState === 'saving'
    const typeOptions = workerTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }))

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <DetailPageBackLink to="/workers" listLabel="workers" />

                <Group justify="space-between" align="flex-start">
                    <Title order={1}>{worker.name}</Title>

                    {pageState === 'reading' && isAdmin(user) && (
                        <Group gap="sm">
                            <Button onClick={handleEdit}>Edit</Button>
                            {worker.archivedAt == null ? (
                                <Button
                                    variant="subtle"
                                    color="red"
                                    onClick={() => setArchiveModalOpen(true)}
                                >
                                    Archive
                                </Button>
                            ) : (
                                <Button variant="subtle" onClick={() => void handleUnarchive()}>
                                    Unarchive
                                </Button>
                            )}
                        </Group>
                    )}

                    {isEditing && (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                onClick={handleCancel}
                                disabled={pageState === 'saving'}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => void handleSave()}
                                loading={pageState === 'saving'}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>
            </Stack>

            {errorMessage && isEditing && (
                <Alert color="red" title="Could not save worker">
                    {errorMessage}
                </Alert>
            )}

            {pageState === 'reading' ? (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="sm">
                        <div>
                            <Text size="sm" c="dimmed">
                                Name
                            </Text>
                            <Text>{worker.name}</Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Type
                            </Text>
                            <Text
                                component={Link}
                                to="/workers/types/$typeId"
                                params={{typeId: worker.workerTypeId}}
                            >
                                {worker.workerTypeName ?? '—'}
                            </Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Employment classification
                            </Text>
                            <Text>
                                {employmentClassificationLabel(worker.employmentClassification)}
                            </Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Email
                            </Text>
                            <Text>{worker.email ?? '—'}</Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Phone
                            </Text>
                            <Text>{worker.phone ?? '—'}</Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Yard
                            </Text>
                            <Text>
                                {yards.find((yard) => yard.id === worker.homeDispatchYardId)
                                    ?.name ?? '—'}
                            </Text>
                        </div>
                        {worker.isPlaceholder ? <Text c="dimmed">{PLACEHOLDER_LABEL}</Text> : null}
                    </Stack>
                </Paper>
            ) : (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="md">
                        <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                        <Select
                            label="Type"
                            withAsterisk
                            data={typeOptions}
                            {...form.getInputProps('workerTypeId')}
                        />
                        <Select
                            label="Employment classification"
                            withAsterisk
                            placeholder="Select classification"
                            data={employmentClassificationOptions}
                            {...form.getInputProps('employmentClassification')}
                        />
                        {requiresHomeYard(workerTypes, form.values.workerTypeId) ? (
                            <Select
                                label="Yard"
                                withAsterisk
                                placeholder="Select yard"
                                data={yards.map((yard) => ({value: yard.id, label: yard.name}))}
                                {...form.getInputProps('homeDispatchYardId')}
                            />
                        ) : null}
                        <TextInput label="Email" {...form.getInputProps('email')} />
                        <TextInput label="Phone" {...form.getInputProps('phone')} />
                        <Switch
                            label={PLACEHOLDER_LABEL}
                            {...form.getInputProps('isPlaceholder', {type: 'checkbox'})}
                        />
                    </Stack>
                </Paper>
            )}

            {assignmentsLoading && (
                <Group justify="center" py="md">
                    <Loader size="sm" />
                </Group>
            )}

            {assignmentsError && (
                <Alert color="red" title="Could not load assignments">
                    {assignmentsLoadError instanceof Error
                        ? assignmentsLoadError.message
                        : 'Something went wrong.'}
                    <Button
                        onClick={() => void refetchAssignments()}
                        variant="light"
                        size="compact-sm"
                        mt="sm"
                    >
                        Retry
                    </Button>
                </Alert>
            )}

            {assignmentList && !assignmentsLoading && !assignmentsError && (
                <AssignmentStrip
                    assignments={assignmentList.assignments}
                    referenceDate={referenceDate}
                    presentation="schedule"
                    title="Schedule"
                    showLegend
                />
            )}

            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Title order={4}>Recent time entries</Title>

                    {timeEntriesLoading && (
                        <Text size="sm" c="dimmed">
                            Loading...
                        </Text>
                    )}

                    {timeEntriesError && (
                        <Stack gap="sm">
                            <Alert color="red" title="Could not load time entries">
                                {timeEntriesLoadError instanceof Error
                                    ? timeEntriesLoadError.message
                                    : 'Something went wrong.'}
                            </Alert>
                            <Button
                                onClick={() => void refetchTimeEntries()}
                                variant="light"
                                size="compact-sm"
                            >
                                Retry
                            </Button>
                        </Stack>
                    )}

                    {!timeEntriesLoading &&
                        !timeEntriesError &&
                        (timeEntryList?.entries.length === 0 ? (
                            <Text size="sm" c="dimmed">
                                No logged hours yet.
                            </Text>
                        ) : (
                            <Stack gap="xs">
                                {timeEntryList?.entries.map((entry) => (
                                    <Stack key={entry.id} gap={2}>
                                        <Text
                                            component={Link}
                                            to="/jobs/$jobId"
                                            params={{jobId: entry.jobId}}
                                            size="sm"
                                            fw={600}
                                            c="blue"
                                        >
                                            {entry.jobName}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                            {formatTimeEntryRange(entry)}
                                        </Text>
                                    </Stack>
                                ))}
                            </Stack>
                        ))}
                </Stack>
            </Paper>

            <ConfirmDialog
                opened={archiveModalOpen}
                title="Archive worker"
                message={`Are you sure you want to archive ${worker.name}? Archived workers cannot be assigned to new jobs.`}
                confirmLabel="Archive"
                loading={archiveWorker.isPending}
                onConfirm={() => void handleArchiveConfirm()}
                onCancel={() => setArchiveModalOpen(false)}
            />
        </Stack>
    )
}
